import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {environment} from "../../environments/environment";
import {Expense} from "./expense.model";
import {catchError} from "rxjs/operators";
import {ExpenseItem} from "./expense-item.model";
import {ExpenseAttachment} from "./expense-attachment.model";
import {ExpenseApproval} from "./expense-approval.model";
import {ExpenseApprovalWorkflow} from "./expense-approval-workflow.model";
import {APIListWrapper} from "../models/common/api-list-wrapper.model";

export interface ExpensePaymentVoucher {
    voucherId: number;
    voucherNumber: string;
    amount: number;
    allocationStatus: 'DRAFT' | 'APPLIED' | 'CANCELLED' | 'REVERSED';
    documentStatus: string;
}

export interface ExpenseSettlementSummary {
    expenseId: number;
    expenseNumber: string;
    status: string;
    currencyCode: string;
    creditAccountId: number;
    payeeId: number;
    payeeType: string;
    payeeName: string;
    totalAmount: number;
    paidAmount: number;
    openAmount: number;
    paymentVouchers: ExpensePaymentVoucher[];
}

export interface ExpensePaymentVoucherRequest {
    amount: number;
    paymentMode: 'CASH' | 'BANK_TRANSFER' | 'CHEQUE';
    cashAccountId?: number | null;
    fromBankAccountId?: number | null;
}


@Injectable({
    providedIn: 'root'
})
export class ExpenseService {

    private baseUrl = `${environment.restApiUrl}`; // Your base URL for the SalesOrdersEndpoint

    constructor(private http: HttpClient) { }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong.
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.error}`);
        }
        // Return an observable with a user-facing error message.
        return throwError(() => new Error('Something bad happened; please try again later.'));
    }

    // --- EXPENSE Endpoints ---
    createExpense(expense: Expense): Observable<Expense> {
        return this.http.post<Expense>(`${this.baseUrl}expenses`, expense)
            .pipe(
                catchError(this.handleError)
            );
    }

    createAndPostExpense(expense: Expense): Observable<Expense> {
        return this.http.post<Expense>(`${this.baseUrl}expenses/post-directly`, expense);
    }

    getExpenseById(id: number): Observable<Expense> {
        return this.http.get<Expense>(`${this.baseUrl}expenses/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getSettlement(expenseId: number): Observable<ExpenseSettlementSummary> {
        return this.http.get<ExpenseSettlementSummary>(`${this.baseUrl}expenses/${expenseId}/settlement`);
    }

    createPaymentVoucher(expenseId: number, request: ExpensePaymentVoucherRequest): Observable<{voucherId: number}> {
        return this.http.post<{voucherId: number}>(`${this.baseUrl}expenses/${expenseId}/payment-vouchers`, request);
    }

    updatePaymentVoucher(expenseId: number, voucherId: number, request: ExpensePaymentVoucherRequest): Observable<{voucherId: number}> {
        return this.http.put<{voucherId: number}>(`${this.baseUrl}expenses/${expenseId}/payment-vouchers/${voucherId}`, request);
    }

    /**
     * Gets a paginated and filtered list of expenses from the backend.
     * @param owningCompanyId The ID of the company that owns the expenses.
     * @param isVendor Flag to filter by vendor status.
     * @param page The page number to retrieve (0-indexed).
     * @param pageSize The number of items per page.
     * @param searchText Optional text to search for in expense fields.
     * @returns An observable of a paginated list of expenses.
     */
    getAllExpenses(
        owningCompanyId: number,
        isVendor: boolean,
        page: number,
        pageSize: number,
        searchText?: string,
        submittedById?: number,
        crmClientIds?: number[]
    ): Observable<APIListWrapper<Expense>> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId)
            .set('isVendor', isVendor)
            .set('page', page)
            .set('pageSize', pageSize);

        // Only add the searchText parameter if it has a value
        if (searchText) {
            params = params.append('searchText', searchText);
        }

        if (submittedById) {
            params = params.append('submittedById', submittedById);
        }

        crmClientIds?.forEach(id => {
            params = params.append('crmClientIds', id);
        });

        return this.http.get<APIListWrapper<Expense>>(`${this.baseUrl}expenses`, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    getMyExpenses(owningCompanyId: number, submittedById: number, page = 0, pageSize = 25): Observable<APIListWrapper<Expense>> {
        return this.getAllExpenses(owningCompanyId, false, page, pageSize, undefined, submittedById);
    }

    /**
     * Gets a paginated and filtered list of fully approved expenses from the backend.
     * @param owningCompanyId The ID of the company that owns the expenses.
     * @param page The page number to retrieve (0-indexed).
     * @param pageSize The number of items per page.
     * @returns An observable of a paginated list of expenses.
     */
    getFullyApprovedExpenses(
        owningCompanyId: number,
        page: number,
        pageSize: number,
    ): Observable<APIListWrapper<Expense>> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId)
            .set('page', page)
            .set('pageSize', pageSize);
        return this.http.get<APIListWrapper<Expense>>(`${this.baseUrl}expenses/approved`, { params })
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Posts an approved expense to the general ledger.
     * @param expenseId The ID of the expense to post.
     * @param debitAccountId The ID of the account to be debited.
     * @param creditAccountId The ID of the account to be credited.
     * @returns An Observable of the updated Expense object with 'Posted' status.
     */
    postExpenseToJournal(expenseId: number, debitAccountId: number, creditAccountId: number): Observable<Expense> {
        // Construct the full URL for the POST request
        const url = `${this.baseUrl}expenses/${expenseId}/post-to-journal/${debitAccountId}/${creditAccountId}`;

        // Since the backend endpoint doesn't expect a request body, we pass `null` as the body.
        return this.http.post<Expense>(url, null);
    }

    updateExpense(id: number, expense: Expense): Observable<Expense> {
        return this.http.put<Expense>(`${this.baseUrl}expenses/${id}`, expense)
            .pipe(
                catchError(this.handleError)
            );
    }

    deleteExpense(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}expenses/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    submitExpenseForApproval(id: number): Observable<Expense> {
        return this.http.post<Expense>(`${this.baseUrl}expenses/${id}/submit`, {})
            .pipe(
                catchError(this.handleError)
            );
    }

    // --- EXPENSE ITEM Endpoints ---
    createExpenseItem(expenseItem: ExpenseItem): Observable<ExpenseItem> {
        return this.http.post<ExpenseItem>(`${this.baseUrl}expense-items`, expenseItem)
            .pipe(
                catchError(this.handleError)
            );
    }

    getExpenseItemById(id: number): Observable<ExpenseItem> {
        return this.http.get<ExpenseItem>(`${this.baseUrl}expense-items/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getExpenseItemsByExpenseId(expenseId: number): Observable<ExpenseItem[]> {
        return this.http.get<ExpenseItem[]>(`${this.baseUrl}expense-items/by-expense/${expenseId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    updateExpenseItem(id: number, expenseItem: ExpenseItem): Observable<ExpenseItem> {
        return this.http.put<ExpenseItem>(`${this.baseUrl}expense-items/${id}`, expenseItem)
            .pipe(
                catchError(this.handleError)
            );
    }

    deleteExpenseItem(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}expense-items/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    // --- EXPENSE ATTACHMENT Endpoints ---
    createExpenseAttachment(expenseId: number, owningCompanyId: number, attachment: ExpenseAttachment): Observable<ExpenseAttachment> {
        return this.http.post<ExpenseAttachment>(`${this.baseUrl}expense-attachments/expense/${expenseId}/owningCompanyId/${owningCompanyId}`, attachment)
            .pipe(
                catchError(this.handleError)
            );
    }

    getExpenseAttachmentById(id: number): Observable<ExpenseAttachment> {
        return this.http.get<ExpenseAttachment>(`${this.baseUrl}expense-attachments/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getExpenseAttachmentsByExpenseId(expenseId: number): Observable<ExpenseAttachment[]> {
        return this.http.get<ExpenseAttachment[]>(`${this.baseUrl}expense-attachments/by-expense/${expenseId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    deleteExpenseAttachment(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}expense-attachments/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    // --- EXPENSE APPROVAL Endpoints ---
    createExpenseApprovalStep(expenseId: number, approverId: number, order: number): Observable<ExpenseApproval> {
        return this.http.post<ExpenseApproval>(`${this.baseUrl}expense-approvals/expense/${expenseId}/approver/${approverId}/order/${order}`, {})
            .pipe(
                catchError(this.handleError)
            );
    }

    getExpenseApprovalById(id: number): Observable<ExpenseApproval> {
        return this.http.get<ExpenseApproval>(`${this.baseUrl}expense-approvals/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getExpenseApprovalsByExpenseId(expenseId: number): Observable<ExpenseApproval[]> {
        return this.http.get<ExpenseApproval[]>(`${this.baseUrl}expense-approvals/by-expense/${expenseId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getPendingApprovalsForApprover(approverId: number): Observable<Expense[]> {
        return this.http.get<Expense[]>(`${this.baseUrl}expense-approvals/pending-for-approver/${approverId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    updateApprovalStatus(id: number, status: string, comments?: string): Observable<ExpenseApproval> {
        const body = { status, comments };
        return this.http.patch<ExpenseApproval>(`${this.baseUrl}expense-approvals/${id}/status`, body)
            .pipe(
                catchError(this.handleError)
            );
    }

    // --- EXPENSE APPROVAL WORKFLOW Endpoints ---
    createWorkflowStep(workflowStep: ExpenseApprovalWorkflow): Observable<ExpenseApprovalWorkflow> {
        return this.http.post<ExpenseApprovalWorkflow>(`${this.baseUrl}expense-approval-workflows`, workflowStep)
            .pipe(
                catchError(this.handleError)
            );
    }

    getWorkflowStepById(id: number): Observable<ExpenseApprovalWorkflow> {
        return this.http.get<ExpenseApprovalWorkflow>(`${this.baseUrl}expense-approval-workflows/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getWorkflowByCompanyId(owningCompanyId: number): Observable<ExpenseApprovalWorkflow[]> {
        return this.http.get<ExpenseApprovalWorkflow[]>(`${this.baseUrl}expense-approval-workflows/by-company/${owningCompanyId}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    updateWorkflowStep(id: number, workflowStep: ExpenseApprovalWorkflow): Observable<ExpenseApprovalWorkflow> {
        return this.http.put<ExpenseApprovalWorkflow>(`${this.baseUrl}expense-approval-workflows/${id}`, workflowStep)
            .pipe(
                catchError(this.handleError)
            );
    }

    deleteWorkflowStep(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}expense-approval-workflows/${id}`)
            .pipe(
                catchError(this.handleError)
            );
    }
}
